import {
  AUTO_TAGGING,
  CUSTOM_CHANNEL,
  INBOX_TYPES,
  LIBRARY_TYPES,
  NATIVE_TAG,
  PERSONAL_INBOX,
  PERSONAL_LIBRARY,
  TEAM_INBOX,
  TEAM_LIBRARY,
} from '@disco/constants/GroupTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'

import type { BusinessShareStatus } from 'types/group'

import {
  CHANNEL_CUSTOM_SORT,
  CHANNEL_DATE_SORT,
  CHANNEL_TITLE_SORT,
  PLAYLIST_DATE_SORT,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
  TITLE_SORT,
} from '@disco/constants/SortTypes'
import filter from 'lodash/filter'
import flatMap from 'lodash/flatMap'
import intersection from 'lodash/intersection'
import uniqBy from 'lodash/uniqBy'
import type { GROUP_TYPE } from '@disco/constants/GroupTypes'

import type {
  Group,
  GroupId,
  GroupLockSettings,
  GroupWithNesting,
  IndentedGroup,
  SelectedNestedGroupFamily,
} from 'types/group'
import type { Track } from 'types/track'

import { getSlug } from '@disco/utils/string-utils'

/**
 * Checks if any group URLs contain references to specific group IDs.
 *
 * Searches through an array of URLs to determine if any contain
 * API endpoints for the specified group IDs.
 *
 * @param groupUrls - Array of URL strings to search through
 * @param groupIds - Array of group IDs to look for in the URLs
 * @returns True if any URL contains a reference to any of the group IDs
 *
 * @example
 * ```typescript
 * const urls = ['/api/group/123/tracks', '/api/group/456/info']
 * const ids = [123, 789]
 * const contains = groupUrlsContainGroupIds(urls, ids) // true (contains 123)
 * ```
 */
export function groupUrlsContainGroupIds(
  groupUrls: Array<string>,
  groupIds: Array<number | null | undefined>,
): boolean {
  return groupIds.some(groupId =>
    groupUrls.some(url => url.indexOf(`/api/group/${groupId || ''}/`) > -1),
  )
}

/**
 * Finds groups that are common to all provided tracks.
 *
 * Returns an array of group IDs that appear in every track's groups array.
 *
 * @param items - Array of tracks to find common groups for
 * @returns Array of group IDs that are present in all tracks
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { groups: ['group1', 'group2', 'group3'] },
 *   { groups: ['group1', 'group2'] },
 *   { groups: ['group1', 'group4'] }
 * ]
 * const common = getCommonGroups(tracks) // ['group1']
 * ```
 */
export const getCommonGroups = (items: Track[]): string[] => {
  return intersection(...items.map(t => t.groups))
}

/**
 * Generates a human-readable description for a group based on its type and structure.
 *
 * Returns appropriate labels like 'channel', 'folder', 'inbox', etc.
 * based on the group's type and parent relationship.
 *
 * @param group - The group to describe
 * @returns Human-readable description string
 *
 * @example
 * ```typescript
 * const channel = { group_type: 'CUSTOM_CHANNEL', parent: null }
 * describeGroup(channel) // 'channel'
 *
 * const folder = { group_type: 'CUSTOM_CHANNEL', parent: 123 }
 * describeGroup(folder) // 'folder'
 *
 * const library = { group_type: 'PERSONAL_LIBRARY' }
 * describeGroup(library) // 'My Library' or 'Starred items'
 * ```
 */
export function describeGroup(group: Folder): string {
  const { group_type, parent } = group
  if (group_type === CUSTOM_CHANNEL && !parent) {
    return 'channel'
  } else if (group_type === CUSTOM_CHANNEL && parent) {
    return 'folder'
  } else if (INBOX_TYPES.includes(group_type)) {
    return 'inbox'
  } else if (group_type === TEAM_LIBRARY) {
    return 'Team Library'
  } else if (group_type === PERSONAL_LIBRARY) {
    return FRONTEND_VERSION === 'v2' ? 'Starred items' : 'My Library'
  } else {
    // safeguard, shouldn't normally happen
    return 'folder'
  }
}

/**
 * Gets the display name for a group, with special handling for personal library.
 *
 * Returns the internal name if available, otherwise falls back to the regular name.
 * Personal library groups get special "Starred" naming.
 *
 * @param group - The group to get the name for
 * @returns The display name for the group
 *
 * @example
 * ```typescript
 * const group = { internal_name: 'Internal Name', name: 'Public Name' }
 * getGroupName(group) // 'Internal Name'
 *
 * const personalLib = { group_type: 'PERSONAL_LIBRARY', name: 'My Library' }
 * getGroupName(personalLib) // 'Starred'
 * ```
 */
export function getGroupName(group?: Group | Folder): string {
  if (!group) return ''

  if (group.group_type === PERSONAL_LIBRARY) {
    return 'Starred'
  }

  return (group as Group).internal_name || group.name || ''
}

/**
 * Generates a description for multiple groups, providing appropriate pluralization.
 *
 * Returns singular descriptions for single groups, "library" for multiple library types,
 * or a count-based description for mixed folder types.
 *
 * @param groups - Array of groups to describe
 * @returns Human-readable description for the group collection
 *
 * @example
 * ```typescript
 * const singleGroup = [{ group_type: 'TEAM_LIBRARY' }]
 * describeMultipleGroups(singleGroup) // 'Team Library'
 *
 * const libraries = [
 *   { group_type: 'TEAM_LIBRARY' },
 *   { group_type: 'PERSONAL_LIBRARY' }
 * ]
 * describeMultipleGroups(libraries) // 'library'
 *
 * const mixedFolders = [{ group_type: 'CUSTOM_CHANNEL' }, { group_type: 'CUSTOM_CHANNEL' }]
 * describeMultipleGroups(mixedFolders) // '2 folders'
 * ```
 */
export function describeMultipleGroups(groups: Array<Group>): string {
  if (groups.length === 1) {
    // perfect match e.g. 'Team Library'
    const group = groups[0]
    return group ? describeGroup(group) : ''
  } else if (
    groups.length > 1 &&
    groups.every(g => LIBRARY_TYPES.includes(g.group_type))
  ) {
    // `team library` + `my library` = `library`
    return 'library'
  } else {
    // vague description and not factually correct, but does the job
    const num = groups.length
    return `${num} folders`
  }
}

/**
 * Sorts an array of folders based on specified sort parameters.
 *
 * Supports sorting by title (alphabetical) and creation date,
 * with configurable sort direction (ascending/descending).
 *
 * @param folders - Array of folders to sort
 * @param sortParam - Sort parameter (TITLE_SORT, PLAYLIST_DATE_SORT, etc.)
 * @param sortDirection - Sort direction (ASC or DESC)
 * @returns Sorted array of folders
 *
 * @example
 * ```typescript
 * const folders = [
 *   { name: 'Beta', created: '2024-01-02' },
 *   { name: 'Alpha', created: '2024-01-01' }
 * ]
 *
 * const byTitle = sortFolders(folders, 'TITLE_SORT', 'ASC')
 * // [{ name: 'Alpha' }, { name: 'Beta' }]
 *
 * const byDate = sortFolders(folders, 'PLAYLIST_DATE_SORT', 'DESC')
 * // [{ name: 'Beta' }, { name: 'Alpha' }] (newest first)
 * ```
 */
export const sortFolders = <T extends Group | GroupWithNesting>(
  folders: T[],
  sortParam?: SORT | null,
  sortDirection?: SORT_DIRECTION | null,
): T[] => {
  if (!sortParam) return folders

  const lessThan = sortDirection === SORT_DIRECTION_ASC ? -1 : 1
  const moreThan = sortDirection === SORT_DIRECTION_ASC ? 1 : -1

  switch (sortParam) {
    case TITLE_SORT:
      return folders.sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) return lessThan
        if (a.name.toLowerCase() > b.name.toLowerCase()) return moreThan
        return 0
      })
    case PLAYLIST_DATE_SORT:
      return folders.sort((a, b) => {
        return (
          (new Date(a.created).getTime() - new Date(b.created).getTime()) *
          moreThan
        )
      })
    default:
      return folders
  }
}

export type Folder = Pick<Group, 'id' | 'name' | 'parent' | 'group_type'>

/**
 * Converts nested folder structure into a flat list with full path names.
 *
 * Recursively traverses nested folders and creates a flat array where each
 * folder's name includes its full path from the root.
 *
 * @param folders - Array of nested folder structures
 * @param isVisible - Function to determine if a group should be included
 * @param basePath - Base path prefix for folder names (used in recursion)
 * @returns Flat array of folders with full path names
 *
 * @example
 * ```typescript
 * const nested = [
 *   {
 *     id: 1,
 *     name: 'Music',
 *     folders: [
 *       { id: 2, name: 'Rock', folders: [] },
 *       { id: 3, name: 'Jazz', folders: [] }
 *     ]
 *   }
 * ]
 *
 * const flat = folderList(nested, () => true)
 * // [
 * //   { id: 1, name: 'Music' },
 * //   { id: 2, name: 'Music/Rock' },
 * //   { id: 3, name: 'Music/Jazz' }
 * // ]
 * ```
 */
export function folderList(
  folders: Array<GroupWithNesting>,
  isVisible: (group: GroupWithNesting) => boolean,
  basePath = '',
): Folder[] {
  return flatMap(folders, group => {
    const name =
      group.internal_name && group.internal_name.length > 0
        ? group.internal_name
        : group.name
    const path = basePath + name
    const result = isVisible(group)
      ? [
          {
            id: group.id,
            name: path,
            parent: group.parent,
            group_type: group.group_type,
          },
        ]
      : []
    if (!group.folders || group.folders.length === 0) {
      return result
    } else {
      return result.concat(...folderList(group.folders, isVisible, path + '/'))
    }
  })
}

/**
 * Filters out group IDs that have locked content from a list of group IDs.
 *
 * Checks each provided group ID against the master list of groups
 * to determine if it has locked content, and removes those that do.
 *
 * @param groups - Master array of all groups with their properties
 * @param groupIds - Array of group IDs to filter
 * @returns Filtered array of group IDs without locked content
 *
 * @example
 * ```typescript
 * const allGroups = [
 *   { id: 1, lock_content: true, name: 'Locked Group' },
 *   { id: 2, lock_content: false, name: 'Open Group' }
 * ]
 * const ids = [1, 2, 3]
 * filterOutGroupIdsWithLockedContent(allGroups, ids) // returns [2, 3]
 * ```
 */
export const filterOutGroupIdsWithLockedContent = (
  groups: Group[] | GroupWithNesting[],
  groupIds: GroupId[],
): GroupId[] => {
  const byId = new Map<GroupId, Group>()
  for (const g of groups) byId.set(g.id, g)

  const result: GroupId[] = []
  for (const id of groupIds) {
    const group = byId.get(id)
    if (!group) continue
    let current: Group | undefined = group
    while (current?.parent !== null && current?.parent !== undefined) {
      const parent = byId.get(current.parent)
      if (!parent) {
        // match legacy getFolderChannel: orphan parent → undefined → include
        current = undefined
        break
      }
      current = parent
    }
    if (!current?.lock_content) result.push(group.id)
  }
  return result
}

/**
 * Gets a list of enabled and visible folders from a set of nested groups.
 *
 * Filters folders based on visibility function and converts them to a flat list
 * with appropriate path information.
 *
 * @param groups - Master array of all groups
 * @param folders - Array of folders with their nested structure
 * @param isVisible - Function that determines if a group should be visible
 * @returns Array of enabled and visible Folder objects
 *
 * @example
 * ```typescript
 * const allGroups = []; // array of all groups
 * const nestedFolders = []; // array of folders with nesting
 * const isVisible = function(group) { return !group.hidden; };
 * const enabledFolders = getEnabledFolders(allGroups, nestedFolders, isVisible);
 * ```
 */
export function getEnabledFolders(
  groups: Group[],
  folders: GroupWithNesting[],
  // TODO: isVisible shouldn't be required, if not supplied it should just return all items
  isVisible: (group: GroupWithNesting) => boolean,
): Folder[] {
  const list = folderList(folders, isVisible)
  const enabledGroupIds = new Set(
    filterOutGroupIdsWithLockedContent(
      groups,
      list.map(g => g.id),
    ),
  )

  return list.filter(g => enabledGroupIds.has(g.id))
}

/**
 * Returns the visible display name for a group.
 *
 * Provides the appropriate name for display in the UI,
 * which may differ from the internal name based on group type.
 *
 * @param group - The group to get the visible name for
 * @returns The display name string
 *
 * @example
 * ```typescript
 * const library = { name: 'Library', group_type: 'PERSONAL_LIBRARY' }
 * getVisibleName(library) // might return 'Starred' or similar
 *
 * const channel = { name: 'Music', group_type: 'CUSTOM_CHANNEL' }
 * getVisibleName(channel) // returns 'Music'
 * ```
 */
export const getVisibleName = (group: Group): string => {
  return group.internal_name && group.internal_name.trim() !== ''
    ? group.internal_name
    : group.name
}

/**
 * Generates a URL-friendly slug for a group.
 *
 * Creates a consistent, URL-friendly identifier for important groups,
 * ensuring stable URLs even if group names are changed by administrators.
 *
 * @param group - The group to generate a slug for
 * @returns URL-friendly slug string
 *
 * @example
 * ```typescript
 * const teamInbox = { name: 'Custom Inbox Name', group_type: 'TEAM_INBOX' }
 * getGroupSlug(teamInbox) // returns 'team-inbox' (hardcoded for special groups)
 *
 * const customGroup = { name: 'My Music Collection', group_type: 'CUSTOM_CHANNEL' }
 * getGroupSlug(customGroup) // returns 'my-music-collection'
 * ```
 */
// we can't rely that Team Inbox to always exist, as admin can override
// group name and thus links in messages may be invalid, for such special cases
// hardcode some slugs associated with important groups
export const getGroupSlug = (group: Group): string => {
  const { group_type } = group
  if (group_type === PERSONAL_INBOX) return getSlug('Inbox')
  if (group_type === TEAM_INBOX) return getSlug('Team Inbox')
  if (group_type === TEAM_LIBRARY) return getSlug('Team Library')
  if (group_type === PERSONAL_LIBRARY) return getSlug('My Catalog')
  return getSlug(getVisibleName(group))
}

/**
 * Finds the personal library group from an array of groups.
 *
 * Searches through the provided groups to find the one with the PERSONAL_LIBRARY type.
 *
 * @param groups - Array of groups to search through
 * @returns The personal library group, or null/undefined if not found
 *
 * @example
 * ```typescript
 * const groups = [
 *   { id: 1, group_type: 'CUSTOM_CHANNEL', name: 'Channel' },
 *   { id: 2, group_type: 'PERSONAL_LIBRARY', name: 'My Library' }
 * ]
 * const library = getPersonalLibrary(groups) // returns the PERSONAL_LIBRARY group
 * ```
 */
export const getPersonalLibrary = (groups: Group[]): Group | null | undefined =>
  groups.find(group => group.group_type === PERSONAL_LIBRARY)

/**
 * Finds the team library group from an array of groups.
 *
 * Searches through the provided groups to find the one with the TEAM_LIBRARY type.
 *
 * @param groups - Array of groups to search through
 * @returns The team library group, or null/undefined if not found
 *
 * @example
 * ```typescript
 * const groups = [
 *   { id: 1, group_type: 'CUSTOM_CHANNEL', name: 'Channel' },
 *   { id: 2, group_type: 'TEAM_LIBRARY', name: 'Team Library' }
 * ]
 * const library = getTeamLibrary(groups) // returns the TEAM_LIBRARY group
 * ```
 */
export const getTeamLibrary = (groups: Group[]): Group | null | undefined =>
  groups.find(group => group.group_type === TEAM_LIBRARY)

/**
 * Gets all default system groups from an array of groups.
 *
 * Returns groups that are considered system defaults, including
 * personal/team libraries and inboxes.
 *
 * @param groups - Array of groups to filter
 * @returns Array containing only the default system groups
 *
 * @example
 * ```typescript
 * const groups = [
 *   { group_type: 'PERSONAL_LIBRARY' },
 *   { group_type: 'TEAM_INBOX' },
 *   { group_type: 'CUSTOM_CHANNEL' }
 * ]
 * getDefaultGroups(groups) // returns [PERSONAL_LIBRARY, TEAM_INBOX]
 * ```
 */
export const getDefaultGroups = (groups: Group[]): Group[] =>
  groups.filter(group => group.is_default)

/**
 * Finds the parent group of a specified child group.
 *
 * Searches through the provided groups array to find the group
 * that matches the parent ID of the child group.
 *
 * @param childGroup - The child group to find the parent for
 * @param groups - Array of all groups to search through
 * @returns The parent group, or null/undefined if not found
 *
 * @example
 * ```typescript
 * const groups = [
 *   { id: 1, name: 'Parent Folder' },
 *   { id: 2, name: 'Child Folder', parent: 1 }
 * ]
 * const child = groups[1]
 * getParentGroup(child, groups) // returns groups[0]
 * ```
 */
export const getParentGroup = (
  childGroup: Group,
  groups: Group[],
): Group | null | undefined => groups.find(g => g.id === childGroup.parent)

/**
 * Available sorting options for groups/channels.
 *
 * Provides predefined sorting configurations including sort parameter,
 * direction, and display title for group/channel lists.
 *
 * @example
 * ```typescript
 * // Using in a dropdown
 * groupSortOptions.map(option => ({
 *   label: option.title,
 *   value: option.sortParam
 * }))
 *
 * // Finding a specific sort option
 * const newestFirst = groupSortOptions.find(opt => opt.title === 'Newest')
 * ```
 */
export const groupSortOptions = [
  {
    title: 'Oldest',
    sortParam: CHANNEL_DATE_SORT,
    sortDirection: SORT_DIRECTION_ASC,
  },
  {
    title: 'Newest',
    sortParam: CHANNEL_DATE_SORT,
    sortDirection: SORT_DIRECTION_DESC,
  },
  {
    title: 'A-Z',
    sortParam: CHANNEL_TITLE_SORT,
    sortDirection: SORT_DIRECTION_ASC,
  },
  {
    title: 'Z-A',
    sortParam: CHANNEL_TITLE_SORT,
    sortDirection: SORT_DIRECTION_DESC,
  },
  { title: 'Custom', sortParam: CHANNEL_CUSTOM_SORT, sortDirection: null },
] as const

/**
 * Calculates the depth/nesting level of a group in the hierarchy.
 *
 * Determines how many levels deep a group is nested by counting
 * parent relationships up to the root level.
 *
 * @param groups - Array of all groups to search through
 * @param group - The group to calculate depth for
 * @returns The nesting depth (0 for root level, 1 for first level, etc.)
 *
 * @example
 * ```typescript
 * const groups = [
 *   { id: 1, name: 'Root' },
 *   { id: 2, name: 'Level 1', parent: 1 },
 *   { id: 3, name: 'Level 2', parent: 2 }
 * ]
 * getDepthOf(groups, groups[0]) // 0 (root level)
 * getDepthOf(groups, groups[2]) // 2 (nested two levels deep)
 * ```
 */
export function getDepthOf(groups: Group[], group?: Group | null): number {
  let currentGroup = group
  let depth = 0
  while (currentGroup) {
    currentGroup = groups.find(g => g.id === currentGroup?.parent)
    depth++
  }
  return depth - 1
}

/**
 * Finds the top-level parent channel for a group.
 *
 * Recursively traverses up the parent chain until it finds a root-level
 * channel (a group with no parent).
 *
 * @param groups - Array of all groups to search through
 * @param group - The group to find the top-level parent for
 * @returns The top-level parent group, or null/undefined if not found
 *
 * @example
 * ```typescript
 * const groups = [
 *   { id: 1, name: 'Root Channel' },
 *   { id: 2, name: 'Subfolder', parent: 1 },
 *   { id: 3, name: 'Nested Folder', parent: 2 }
 * ]
 * getFolderChannel(groups, groups[2]) // returns groups[0]
 * ```
 */
export function getFolderChannel(
  groups: Group[],
  group?: Group | null,
): Group | null | undefined {
  let currentGroup = group
  while (currentGroup?.parent) {
    currentGroup = groups.find(g => g.id === currentGroup?.parent)
  }
  return currentGroup
}

/**
 * Finds the topmost parent channel for a nested folder.
 *
 * Traverses up the parent chain until it reaches a root-level group with no parent.
 *
 * @param groups - Array of all groups to search within
 * @param group - The child group to find the parent channel for
 * @returns The root channel that contains this group, or null/undefined if not found
 *
 * @example
 * ```typescript
 * const rootChannel = getNestedFolderChannel(allGroups, nestedFolder);
 * ```
 */
export function getNestedFolderChannel(
  groups: GroupWithNesting[],
  group?: GroupWithNesting | null,
): GroupWithNesting | null | undefined {
  let currentGroup = group
  while (currentGroup?.parent) {
    currentGroup = groups.find(g => g.id === currentGroup?.parent)
  }
  return currentGroup
}
/**
 * Finds all parent channels for a set of group IDs.
 *
 * Maps the provided group IDs to their corresponding groups, then finds the
 * parent channel for each group.
 *
 * @param groups - Array of all groups to search within
 * @param groupIds - Array of group IDs to find parent channels for
 * @returns Array of parent channel groups
 *
 * @example
 * ```typescript
 * const parentChannels = getParentChannels(allGroups, [123, 456]);
 * ```
 */
export const getParentChannels = (
  groups: Array<Group>,
  groupIds: Array<GroupId>,
): Group[] => {
  const targetGroups = groupIds
    .map(id => groups.find(g => g.id == id))
    .filter((g): g is Group => g !== undefined)

  return targetGroups
    .map(targetGroup => getFolderChannel(groups, targetGroup))
    .filter((g): g is Group => g !== undefined)
}

/**
 * Flattens a nested group structure into a single-level array.
 *
 * Recursively processes all nested folders and combines them with parent groups.
 *
 * @param groups - Array of groups with nested structure
 * @returns Flattened array containing all groups and their nested subgroups
 *
 * @example
 * ```typescript
 * const allGroups = flattenGroups(nestedGroups);
 * ```
 */
export function flattenGroups(groups: GroupWithNesting[]): GroupWithNesting[] {
  return flatMap(groups, group => {
    const subGroups = group?.folders ? flattenGroups(group.folders) : []
    return [group, ...subGroups]
  })
}

/**
 * Flattens nested groups into a single-level array.
 *
 * Similar to flattenGroups but with slight implementation differences.
 *
 * @param groups - Array of groups with nested structure
 * @returns Flattened array containing all groups and subgroups
 *
 * @example
 * ```typescript
 * const allGroups = flattenNestedGroups(nestedGroups);
 * ```
 */
export function flattenNestedGroups(
  groups: GroupWithNesting[],
): GroupWithNesting[] {
  return flatMap(groups, group => {
    const subGroups = group.folders ? flattenGroups(group.folders) : []
    return [group, ...subGroups]
  })
}

/**
 * Finds child folders of a group matching a search term.
 *
 * @param group - Group with nested folders
 * @param searchTerm - Search string to match folder names
 * @returns Array of child folders matching the search
 *
 * @example
 * ```typescript
 * getChildFolders(group, 'project')
 * ```
 */
export function getChildFolders(
  group: GroupWithNesting,
  searchTerm: string,
): GroupWithNesting[] {
  const flattenedGroups = flattenNestedGroups([group])

  const isChildFolder = flattenedGroups.filter(
    f => f.parent && f.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const childFolders = (group.folders || []).filter(f =>
    isChildFolder.some(c => f.name.includes(c.name)),
  )

  return childFolders
}

/**
 * Checks if a folder name matches a search term (case-insensitive).
 *
 * @param name - Folder name
 * @param searchTerm - Optional search string
 * @returns True if name includes searchTerm
 *
 * @example
 * ```typescript
 * isFolderMatchingSearchTerm('Project X', 'proj') // true
 * ```
 */
export const isFolderMatchingSearchTerm = (
  name: string,
  searchTerm?: string,
): boolean => {
  if (searchTerm && searchTerm.length) {
    return Boolean(name.toLowerCase().includes(searchTerm.toLowerCase()))
  }
  return false
}

/**
 * Filters and returns groups matching a search value.
 *
 * @param groups - Array of groups to search
 * @param value - Search string
 * @returns Array of matching groups, or null/undefined
 *
 * @example
 * ```typescript
 * getChannelsBySearch(groups, 'team')
 * ```
 */
export function getChannelsBySearch(
  groups: Group[],
  value: string,
): Group[] | null | undefined {
  let filteredChannels: Array<Group> = []
  const channels = groups.filter(g => g.group_type === CUSTOM_CHANNEL)
  const allGroups = flattenGroups(channels as GroupWithNesting[])
  const searchChannels = filter(
    allGroups,
    group =>
      group.is_auto_tagged ||
      (group.name && group.name.toLowerCase().includes(value.toLowerCase())),
  ) as GroupWithNesting[]

  const parentChannels = getParentChannels(
    channels,
    searchChannels.map(c => c.id),
  )
  filteredChannels = [...searchChannels, ...parentChannels]
  filteredChannels = uniqBy(filteredChannels, 'id') as Group[]
  return filteredChannels
}

export const isGroupMatchingSearchTerm = (
  group: GroupWithNesting,
  term: string,
) => {
  const lowerTerm = term?.toLowerCase()
  return (group.name || group.internal_name).toLowerCase().includes(lowerTerm)
}

export function getGroupsMatchingSearchTerm(
  groups: GroupWithNesting[],
  term: string,
): GroupWithNesting[] {
  if (!term) return groups

  const allGroups = flattenNestedGroups(groups)

  const searchGroups = allGroups.filter(group =>
    isGroupMatchingSearchTerm(group, term),
  )

  const filteredNestedGroups = [
    ...searchGroups,
    ...getParentChannels(
      groups,
      searchGroups.map(g => g.id),
    ),
  ]

  return uniqBy(filteredNestedGroups, 'id') as GroupWithNesting[]
}

export type ChannelTreeItem = {
  channel: GroupWithNesting
  folders: GroupWithNesting[]
  subFolders: GroupWithNesting[]
}

export const getChannelAndFoldersAndSubFoldersMatchingSearchTerm = (
  channel: GroupWithNesting,
  searchTerm: string,
): ChannelTreeItem[] | null => {
  if (!channel) return null
  const folders = (channel.folders ?? []).filter(f => !f.is_auto_tagged)
  const subFolders = folders
    .flatMap(f => f.folders ?? [])
    .filter(sf => !sf.is_auto_tagged)
  if (!searchTerm) return [{ channel, folders, subFolders }]

  const lowerSearch = searchTerm.toLowerCase()
  const channelMatch = getGroupName(channel).toLowerCase().includes(lowerSearch)
  const matchedFolders = folders.filter(f =>
    getGroupName(f).toLowerCase().includes(lowerSearch),
  )
  const matchedSubs = subFolders.filter(sf =>
    getGroupName(sf).toLowerCase().includes(lowerSearch),
  )
  if (!channelMatch && matchedFolders.length === 0 && matchedSubs.length === 0)
    return []

  // Channel-name match shows all children; otherwise only matched ones.
  return [
    {
      channel,
      folders: channelMatch ? folders : matchedFolders,
      subFolders: channelMatch ? subFolders : matchedSubs,
    },
  ]
}

/**
 * Ensures a group has a folders property (for nesting).
 *
 * @param group - Group or nested group
 * @returns GroupWithNesting object
 *
 * @example
 * ```typescript
 * getGroupWithNesting(group)
 * ```
 */
export function getGroupWithNesting(
  group: Group | GroupWithNesting,
): GroupWithNesting {
  return (
    (group as GroupWithNesting).folders ? group : { ...group, folders: [] }
  ) as GroupWithNesting
}

/**
 * Aggregates lock restrictions for a set of groups.
 *
 * @param groups - Array of groups
 * @param groupIds - Group IDs to check
 * @returns Object with lock_content and lock_structure flags
 *
 * @example
 * ```typescript
 * getGroupsRestrictions(groups, [1, 2])
 * ```
 */
export function getGroupsRestrictions(
  groups: Group[],
  groupIds: GroupId[],
): GroupLockSettings {
  const parentChannels = getParentChannels(groups, groupIds)

  const restrictions = {
    lock_content: false,
    lock_structure: false,
  }
  parentChannels.forEach(c => {
    if (c?.lock_content) restrictions.lock_content = true
    if (c?.lock_structure) restrictions.lock_structure = true
  })

  return restrictions
}

/**
 * Checks if groups contain both locked and regular channels.
 *
 * Determines whether the group collection includes both locked (restricted access)
 * and regular (open access) channels, useful for UI state management.
 *
 * @param groups - Array of groups to check
 * @returns True if groups contain both locked and regular channels
 *
 * @example
 * ```typescript
 * const groups = [
 *   { locked: true, name: 'Private Channel' },
 *   { locked: false, name: 'Public Channel' }
 * ]
 * groupsContainLockedAndRegularChannels(groups) // true
 * ```
 */
export function groupsContainLockedAndRegularChannels(
  groups: Group[],
  groupIds: GroupId[],
): boolean {
  const parentChannels = getParentChannels(groups, groupIds)

  const supplyChannelsPresent = !!parentChannels.find(c => c?.lock_content)
  const regularChannelPresent = !!parentChannels.find(c => c && !c.lock_content)

  return supplyChannelsPresent && regularChannelPresent
}

/**
 * Extracts a group ID from a URL string.
 *
 * Parses URLs to extract group/channel identifiers, useful for
 * routing and navigation within the application.
 *
 * @param url - The URL string to extract group ID from
 * @returns The extracted group ID, or null if not found
 *
 * @example
 * ```typescript
 * getGroupIdFromUrl('/channel/123/tracks') // 123
 * getGroupIdFromUrl('/library/456') // 456
 * getGroupIdFromUrl('/invalid-url') // null
 * ```
 */
export const getGroupIdFromUrl = (url: string): GroupId | null | undefined => {
  const matches = url.match(/(group\/)(\d+)(\/)?$/)
  if (matches && matches.length === 4 && matches[2])
    return parseInt(matches[2], 10)
  return null
}

/**
 * Converts an array of URL strings to group IDs.
 *
 * Maps over URL strings and extracts group IDs from each,
 * filtering out any invalid URLs that don't contain group IDs.
 *
 * @param urls - Array of URL strings to convert
 * @returns Array of extracted group IDs
 *
 * @example
 * ```typescript
 * const urls = ['/channel/123', '/library/456', '/invalid']
 * convertGroupStringsToGroupIds(urls) // [123, 456]
 * ```
 */
export const convertGroupStringsToGroupIds = (urls: string[]): GroupId[] => {
  const ids = urls.map(url => getGroupIdFromUrl(url))

  return ids.reduce<Array<any>>((arr, id) => (id ? [...arr, id] : arr), [])
}

/**
 * Checks if an item requires adding to the team library.
 *
 * Determines whether a track or item needs to be added to the team library
 * based on its current location and team library requirements.
 *
 * @param itemGroups - Array of group URLs for the item
 * @param groups - Array of available groups
 * @returns True if item requires adding to team library
 *
 * @example
 * ```typescript
 * const itemGroups = ['/group/personal-123']
 * const groups = [{ id: 'team-456', group_type: 'team' }]
 * itemRequireAddingToTeamLib(itemGroups, groups) // true if not in team lib
 * ```
 */
export const itemRequireAddingToTeamLib = (
  itemGroups: string[],
  groups: Group[],
): boolean => {
  if (!itemGroups) return false
  if (itemGroups.length === 0) return true
  return !itemGroups.some(url => {
    const fullGroup = groups.find(
      f => getGroupIdFromUrl(f.url) === getGroupIdFromUrl(url),
    )
    return (
      fullGroup &&
      ![...INBOX_TYPES, AUTO_TAGGING, NATIVE_TAG].includes(fullGroup.group_type)
    )
  })
}

/**
 * Checks if an item is automatically tagged.
 *
 * Determines whether a track or item has been automatically tagged
 * by the system based on its metadata or other automated processes.
 *
 * @param itemGroups - Array of group URLs for the item
 * @param groups - Array of available groups
 * @returns True if item is automatically tagged
 *
 * @example
 * ```typescript
 * const itemGroups = ['/group/auto-tagged-channel']
 * const groups = [{ id: 1, is_auto_tagged: true }]
 * itemIsAutoTagged(itemGroups, groups) // true
 * ```
 */
export const itemIsAutoTagged = (
  itemGroups: string[],
  groups: Group[],
): boolean => {
  if (!itemGroups) return false
  return itemGroups.some(url => {
    const fullGroup = groups.find(
      f => getGroupIdFromUrl(f.url) === getGroupIdFromUrl(url),
    )
    return fullGroup && fullGroup.is_auto_tagged
  })
}

/**
 * Maps groups to a nested structure with folders arrays.
 *
 * Transforms a flat array of groups into a structure that supports
 * nested folder hierarchies by adding empty folders arrays.
 *
 * @param groups - Array of groups to map
 * @returns Array of groups with nesting structure and empty folders arrays
 *
 * @example
 * ```typescript
 * const flatGroups = [{ id: 1, name: 'Channel 1' }, { id: 2, name: 'Channel 2' }]
 * const nestedGroups = mapGroups(flatGroups)
 * // Each group now has: { ...group, folders: [] }
 * ```
 */
export const mapGroups = (groups: Group[]): GroupWithNesting[] => {
  const mappedGroups: GroupWithNesting[] = []
  groups.forEach(g => {
    mappedGroups[g.id] = { ...g, folders: [] }
  })
  return mappedGroups
}

type ExpandedFolder = {
  [id: number]: boolean
}

/**
 * Expands folders in search results based on search term matching.
 *
 * Determines which folders should be expanded/collapsed in a tree view
 * based on whether they or their children match the search term.
 *
 * @param channels - Array of indented groups with folder structure
 * @param searchTerm - Search string to match against folder names
 * @returns Object mapping folder IDs to their expanded state (true = expanded)
 *
 * @example
 * ```typescript
 * const channels = [
 *   { id: 1, name: 'Music', folders: [{ id: 2, name: 'Rock' }] }
 * ]
 * const expanded = expandSearchFolder(channels, 'rock')
 * // Returns: { 1: true, 2: false } - parent expanded, match not expanded
 * ```
 */
export const expandSearchFolder = (
  channels: IndentedGroup[],
  searchTerm: string,
): ExpandedFolder => {
  const flattenChannels = flatMap(channels, c => {
    if (!(c as any).folders) {
      return [c]
    }

    if ((c as any).folders) {
      const subfolders = flatMap((c as any).folders, f => f.folders || [])
      return [c, ...(c as any).folders, ...subfolders]
    }

    return [c]
  }).filter(Boolean) // Remove any undefined/null items

  const expandedFolders = flattenChannels.map(f => {
    const isChannelMatchingSearchTerm = isFolderMatchingSearchTerm(
      f?.name || '',
      searchTerm,
    )
    return {
      [f.id]: searchTerm.length > 1 ? !isChannelMatchingSearchTerm : false,
    }
  })

  return Object.assign({}, ...expandedFolders)
}

const validSharedStatuses: BusinessShareStatus[] = ['accepted', 'pending']

/**
 * Checks if a channel is shared.
 *
 * Determines whether a group/channel is shared with other users or teams,
 * as opposed to being private to the current user.
 *
 * @param group - The group/channel to check for sharing status
 * @returns True if channel is shared, false if private
 *
 * @example
 * ```typescript
 * const sharedChannel = { business_role: 'partner', name: 'Team Beats' }
 * const privateChannel = { shared_with_businesses: [], name: 'My Private Library' }
 *
 * isChannelShared(sharedChannel) // true
 * isChannelShared(privateChannel) // false
 * isChannelShared(undefined) // false
 * ```
 */
export const isChannelShared = (
  group?: Group | IndentedGroup | null,
): boolean => {
  if (!group) return false
  if (group.business_role === 'partner') return true
  if (!group.shared_with_businesses?.length) return false
  return group.shared_with_businesses.some(business =>
    validSharedStatuses.includes(business.status),
  )
}

export const getSelectedNestedGroupFamily = (
  groups: GroupWithNesting[],
  group?: GroupWithNesting | IndentedGroup | null,
): SelectedNestedGroupFamily | null => {
  if (!group) return null

  const channel = getNestedFolderChannel(groups, group)
  if (!channel) return null

  // Selected node is the channel itself → no folder/subFolder.
  if (channel.id === group.id) {
    return { parent: channel, folder: null, subFolder: null }
  }

  const folder =
    channel.folders?.find(f => f.id === group.id || f.id === group.parent) ??
    null

  const subFolder = folder?.folders?.find(f => f.id === group.id) ?? null

  return { parent: channel, folder, subFolder }
}
